// src/services/captureApi.js
//
// Two clients on purpose:
//
//  • The desktop half uses the shared authenticated `API` instance.
//  • The phone half uses a bare axios client. The phone page is public — it must
//    never send the desktop's JWT, and it must never be caught by API's 401
//    interceptor, which redirects to /login. The session token in the URL is the
//    phone's only credential.

import axios from 'axios';
import API from './api';
import ConfigService from './ConfigService';
import { headerSafeFileName } from '../utils/headerSafeFileName';

/**
 * Hosts only reachable from the machine itself or its LAN. Used to decide
 * whether the dev overrides apply at all.
 */
const isLocalHost = (origin = '') =>
  /^https?:\/\/(localhost|127\.\d+\.\d+\.\d+|\[::1\]|0\.0\.0\.0|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+|[^/]+\.local)(:|\/|$)/i
    .test(origin);

// ConfigService classifies any host that is not localhost/127.0.0.1/*.local as a
// PRODUCTION client, so a phone opening this page over a LAN IP would resolve the
// PRODUCTION backend even in dev. REACT_APP_CAPTURE_API_URL names the backend
// explicitly for that case — and, like the base-URL override, is honoured only on
// a local/LAN desktop so a stale dev value can never redirect live traffic.
const phoneApiBase = () => {
  const explicit = isLocalHost(window.location.origin) ? process.env.REACT_APP_CAPTURE_API_URL : null;
  return (explicit || ConfigService.getBackendUrl()).replace(/\/+$/, '');
};

const phoneClient = () =>
  axios.create({ baseURL: `${phoneApiBase()}/api/v1`, timeout: 60000 });

// ── Desktop ───────────────────────────────────────────────────────────────

/**
 * Open a capture session for one upload target and get back the token to render
 * as a QR code.
 *
 * @param {Object} opts
 * @param {string} opts.targetKey   stable id used to route the result, e.g. 'walkInCase.goldImages'
 * @param {string} opts.targetLabel shown on the phone, e.g. 'Gold Images — Walk-In Case'
 * @param {string} opts.folderPath  ImageKit folder the phone uploads into
 * @param {string} [opts.recordId]
 * @param {string} [opts.slot]      sub-target, e.g. the document type
 * @param {string} [opts.accept]
 * @param {number} [opts.maxFiles]
 * @param {boolean} [opts.requireGeo]
 */
export const createCaptureSession = (opts) =>
  API.post('/capture-sessions', opts).then((res) => res.data.data);

export const getCaptureSession = (token) =>
  API.get(`/capture-sessions/${token}`).then((res) => res.data.data);

export const closeCaptureSession = (token) =>
  API.post(`/capture-sessions/${token}/close`).then((res) => res.data.data);

// ── Phone ─────────────────────────────────────────────────────────────────

export const fetchPublicCaptureSession = (token) =>
  phoneClient().get(`/capture-sessions/public/${token}`).then((res) => res.data.data);

/**
 * Upload one captured file. Coordinates, when available, are sent as headers so
 * the geo-tag comes from the device that took the photo rather than from EXIF.
 */
export const uploadCaptureFile = (token, file, { fileName, geo } = {}) => {
  const headers = {
    'Content-Type': 'application/octet-stream',
    'x-file-name': headerSafeFileName(fileName || file.name, `capture-${Date.now()}.jpg`),
  };
  if (geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng)) {
    headers['x-geo-lat'] = String(geo.lat);
    headers['x-geo-lng'] = String(geo.lng);
    if (Number.isFinite(geo.accuracy)) headers['x-geo-accuracy'] = String(geo.accuracy);
  }
  return phoneClient()
    .post(`/capture-sessions/public/${token}/files`, file, {
      headers,
      transformRequest: [(data) => data],
      maxBodyLength: Infinity,
    })
    .then((res) => res.data.data);
};

export const deleteCaptureFile = (token, fileId) =>
  phoneClient().delete(`/capture-sessions/public/${token}/files/${fileId}`).then((res) => res.data.data);

export const finishCaptureSession = (token) =>
  phoneClient().post(`/capture-sessions/public/${token}/done`).then((res) => res.data.data);

/**
 * The URL the QR encodes. Must be reachable from a phone, so it cannot be
 * localhost — set REACT_APP_CAPTURE_BASE_URL to the CRM's public frontend
 * origin in any environment where the desktop is not on a routable host.
 */
/**
 * The URL the QR encodes.
 *
 * In production this is always window.location.origin — the desktop reached the
 * CRM there, so a phone on the internet can too. The REACT_APP_CAPTURE_BASE_URL
 * override is honoured ONLY when the desktop is itself on a local/LAN host,
 * because that is the only case it exists for. Ignoring it otherwise means a
 * dev value accidentally baked into a production build (a shared .env is read
 * by `npm run build` too) can never hijack a live QR code.
 */
export const captureUrlFor = (token) => {
  const origin = window.location.origin;
  const override = isLocalHost(origin) ? process.env.REACT_APP_CAPTURE_BASE_URL : null;
  const base = (override || origin).replace(/\/+$/, '');
  return `${base}/capture/${token}`;
};

/** True when the QR would encode a host no phone on mobile data can resolve. */
export const captureBaseIsLocal = () => {
  const origin = window.location.origin;
  const override = isLocalHost(origin) ? process.env.REACT_APP_CAPTURE_BASE_URL : null;
  return isLocalHost(override || origin);
};
